/**
 * Delimited text in and out: CSV and TSV with RFC 4180 quoting, any custom delimiter,
 * runs of whitespace, and fixed-width columns.
 *
 * RFC 4180 is the closest thing CSV has to a standard: a field may be wrapped in
 * double quotes, and inside quotes the delimiter and line breaks are ordinary text and
 * a doubled quote ("") is one quote character. Real files bend it, so the parser is
 * lenient where that is safe — a quote in the middle of an unquoted field is kept as
 * text, and text after a closing quote is appended — and reports what it repaired.
 */

export interface ParsedTable {
  rows: string[][];
  /** A quoted field ran to the end of the text without its closing quote. */
  unterminatedQuote: boolean;
  /** Rows where text followed a closing quote before the next delimiter. */
  strayQuoteRows: number;
}

const BOM = "﻿";
const QUOTE = '"';

function isLineBreak(character: string | undefined): boolean {
  return character === "\n" || character === "\r";
}

/** Length of the line break at `index`: 2 for CRLF, 1 for LF or CR. */
function lineBreakLength(text: string, index: number): number {
  return text[index] === "\r" && text[index + 1] === "\n" ? 2 : 1;
}

interface QuotedField {
  value: string;
  /** Index just past the closing quote (or the end of the text). */
  end: number;
  closed: boolean;
}

function readQuoted(text: string, start: number): QuotedField {
  let value = "";
  let index = start + 1;
  while (index < text.length) {
    const quoteAt = text.indexOf(QUOTE, index);
    if (quoteAt === -1) {
      return {
        value: value + text.slice(index),
        end: text.length,
        closed: false,
      };
    }
    value += text.slice(index, quoteAt);
    if (text[quoteAt + 1] === QUOTE) {
      value += QUOTE;
      index = quoteAt + 2;
      continue;
    }
    return { value, end: quoteAt + 1, closed: true };
  }
  return { value, end: text.length, closed: false };
}

/** Index of the next delimiter or line break at or after `start`. */
function fieldEnd(text: string, start: number, delimiter: string): number {
  let index = start;
  while (index < text.length) {
    if (isLineBreak(text[index]) || text.startsWith(delimiter, index)) {
      return index;
    }
    index++;
  }
  return index;
}

/**
 * Parse delimited text into rows of fields. `delimiter` may be several characters
 * long (" | ", "::"). Pass `quotes: false` for data that uses " as an ordinary
 * character.
 */
export function parseDelimited(
  input: string,
  delimiter: string,
  quotes = true
): ParsedTable {
  const text = input.startsWith(BOM) ? input.slice(1) : input;
  const rows: string[][] = [];
  if (text.length === 0) {
    return { rows, unterminatedQuote: false, strayQuoteRows: 0 };
  }
  let row: string[] = [];
  let index = 0;
  let unterminatedQuote = false;
  let strayQuoteRows = 0;
  let strayInRow = false;
  const separator = delimiter.length > 0 ? delimiter : ",";

  while (index <= text.length) {
    let value: string;
    if (quotes && text[index] === QUOTE) {
      const quoted = readQuoted(text, index);
      unterminatedQuote ||= !quoted.closed;
      const end = fieldEnd(text, quoted.end, separator);
      if (end > quoted.end) {
        strayInRow = true;
      }
      value = quoted.value + text.slice(quoted.end, end);
      index = end;
    } else {
      const end = fieldEnd(text, index, separator);
      value = text.slice(index, end);
      index = end;
    }
    row.push(value);
    if (index < text.length && text.startsWith(separator, index)) {
      index += separator.length;
      continue;
    }
    rows.push(row);
    strayQuoteRows += strayInRow ? 1 : 0;
    row = [];
    strayInRow = false;
    if (index >= text.length) {
      break;
    }
    index += lineBreakLength(text, index);
    if (index >= text.length) {
      break;
    }
  }
  return { rows, unterminatedQuote, strayQuoteRows };
}

const LINE_SPLIT = /\r\n|\r|\n/;
const WHITESPACE_RUN = /[ \t]+/;

function lines(input: string): string[] {
  const text = input.startsWith(BOM) ? input.slice(1) : input;
  const all = text.split(LINE_SPLIT);
  return all.at(-1) === "" ? all.slice(0, -1) : all;
}

/** Split each line on runs of spaces and tabs, ignoring leading and trailing ones. */
export function parseWhitespace(input: string): string[][] {
  return lines(input).map((line) => {
    const trimmed = line.trim();
    return trimmed ? trimmed.split(WHITESPACE_RUN) : [""];
  });
}

/**
 * Split each line at fixed character positions. `starts` are 0-based column starts;
 * a cell runs to the next start. Cells are trimmed of surrounding spaces.
 */
export function parseFixedWidth(
  input: string,
  starts: readonly number[]
): string[][] {
  const sorted = [...new Set([0, ...starts])]
    .filter((start) => start >= 0)
    .sort((a, b) => a - b);
  return lines(input).map((line) => {
    const characters = [...line];
    return sorted.map((start, position) =>
      characters
        .slice(start, sorted[position + 1] ?? characters.length)
        .join("")
        .trim()
    );
  });
}

/**
 * Column starts for fixed-width text: every position where a column that is blank in
 * all non-empty lines is followed by one that is not. Reads tables that line up with
 * spaces, as terminals and PDF exports print them.
 */
export function detectFixedWidthStarts(input: string): number[] {
  const rowsOfCharacters = lines(input)
    .filter((line) => line.trim().length > 0)
    .map((line) => [...line]);
  const width = Math.max(0, ...rowsOfCharacters.map((row) => row.length));
  const blank: boolean[] = [];
  for (let column = 0; column < width; column++) {
    blank.push(
      rowsOfCharacters.every((row) => (row[column] ?? " ").trim() === "")
    );
  }
  const starts = [0];
  for (let column = 1; column < width; column++) {
    if (blank[column - 1] && !blank[column]) {
      starts.push(column);
    }
  }
  return starts;
}

/** The delimiters auto-detection chooses between. */
export const DETECTABLE_DELIMITERS = [",", "\t", ";", "|"] as const;
const DETECTION_LINES = 20;

/**
 * The likeliest delimiter: the candidate that splits the first lines into the same
 * number of fields (more than one) most often, quotes respected. Comma when nothing
 * splits — a one-column CSV is still a CSV.
 */
export function detectDelimiter(
  input: string,
  candidates: readonly string[] = DETECTABLE_DELIMITERS
): string {
  const sample = lines(input)
    .filter((line) => line.trim().length > 0)
    .slice(0, DETECTION_LINES)
    .join("\n");
  let best = ",";
  let bestScore = 0;
  for (const candidate of candidates) {
    const counts = parseDelimited(sample, candidate).rows.map(
      (row) => row.length
    );
    const first = counts[0] ?? 1;
    if (first < 2) {
      continue;
    }
    const consistent = counts.filter((count) => count === first).length;
    const score = consistent * 100 + first;
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

export type QuoteMode = "needed" | "always" | "never";

/**
 * One field for output. With "needed", quoted when it contains the delimiter, a quote or
 * a line break, or starts or ends with a space (which spreadsheet imports may trim),
 * with any quote inside doubled. "never" writes the raw value, for a plain list.
 */
export function quoteField(
  value: string,
  delimiter: string,
  mode: QuoteMode = "needed"
): string {
  if (mode === "never") {
    return value;
  }
  const needsQuotes =
    mode === "always" ||
    (delimiter.length > 0 && value.includes(delimiter)) ||
    value.includes(QUOTE) ||
    value.includes("\n") ||
    value.includes("\r") ||
    value.startsWith(" ") ||
    value.endsWith(" ");
  return needsQuotes
    ? `${QUOTE}${value.replaceAll(QUOTE, '""')}${QUOTE}`
    : value;
}

/** Rows back to delimited text, one line per row. */
export function toDelimited(
  rows: readonly (readonly string[])[],
  delimiter: string,
  mode: QuoteMode = "needed"
): string {
  return rows
    .map((row) =>
      row.map((value) => quoteField(value, delimiter, mode)).join(delimiter)
    )
    .join("\n");
}
